import { useCallback, useEffect, useState } from "react";
import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";
import { fetchGoogleRoute, type GoogleRoute } from "@/api/googleDirections";

export type GoogleComparisonStatus = "idle" | "loading" | "ready" | "error";

/**
 * Fetch the Google Maps route for a given A→B once per trip and keep it
 * in component state. Returns:
 *   - `route`   — the GoogleRoute payload (null until ready / on error)
 *   - `status`  — explicit lifecycle ("idle" | "loading" | "ready" | "error")
 *   - `loading` — convenience boolean (true during in-flight fetch)
 *   - `retry()` — manual refetch, used by the Google slot's "Retry" button
 *                 when the call fails (quota / network / no key).
 *
 * The Google slot in the route list is ALWAYS rendered (the user's
 * contract is "one option always = Google Maps"), so a failed fetch is
 * a first-class state, not an empty fallback.
 */
export function useGoogleComparison(
  start: LngLat | undefined,
  end: LngLat | undefined,
  mode: TravelMode,
): {
  route: GoogleRoute | null;
  status: GoogleComparisonStatus;
  loading: boolean;
  retry: () => void;
} {
  const [route, setRoute] = useState<GoogleRoute | null>(null);
  const [status, setStatus] = useState<GoogleComparisonStatus>("idle");
  // Bumping `attempt` re-runs the effect with the same start/end/mode,
  // giving us a clean "retry" without duplicating the fetch logic.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!start || !end) {
      setRoute(null);
      setStatus("idle");
      return;
    }
    let cancelled = false;
    // Clear the PREVIOUS trip's route immediately (stale-polyline fix). Without
    // this, the old Google/Direct line — and its stale distance/duration in the
    // Direct card — stayed on screen until the new fetch resolved. On a long or
    // far-away trip that fetch is slow, so the wrong-location line lingered
    // visibly. Dropping to null now means the line disappears the instant the
    // trip changes and only redraws when the fresh route is ready.
    setRoute(null);
    setStatus("loading");
    fetchGoogleRoute(start, end, mode)
      .then((r) => {
        if (cancelled) return;
        if (r) {
          setRoute(r);
          setStatus("ready");
        } else {
          setRoute(null);
          setStatus("error");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setRoute(null);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start?.[0], start?.[1], end?.[0], end?.[1], mode, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { route, status, loading: status === "loading", retry };
}
